var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BasePortableFakeFS, ppath, npath, CustomDir, } from '@yarnpkg/fslib';
import { ZipOpenFS } from '@yarnpkg/libzip';
import { resolve, toNamespacedPath } from 'path';
import { constants } from 'fs';
const uniqBy = (arr, pick) => {
    const seen = new Set();
    return arr.filter((x) => {
        const key = pick(x);
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
};
function uniqReaddir(arr) {
    return uniqBy(arr, (s) => (typeof s === 'string' ? s : s.name));
}
export class SnapshotZipFS extends BasePortableFakeFS {
    constructor(opts) {
        super();
        this.fdMap = new Map();
        this.nextFd = 3;
        this.accessPromise = ZipOpenFS.prototype.accessPromise;
        this.accessSync = ZipOpenFS.prototype.accessSync;
        this.appendFilePromise = ZipOpenFS.prototype.appendFilePromise;
        this.appendFileSync = ZipOpenFS.prototype.appendFileSync;
        this.chmodPromise = ZipOpenFS.prototype.chmodPromise;
        this.chmodSync = ZipOpenFS.prototype.chmodSync;
        this.fchmodPromise = ZipOpenFS.prototype.fchmodPromise;
        this.fchmodSync = ZipOpenFS.prototype.fchmodSync;
        this.chownPromise = ZipOpenFS.prototype.chownPromise;
        this.chownSync = ZipOpenFS.prototype.chownSync;
        this.fchownPromise = ZipOpenFS.prototype.fchownPromise;
        this.fchownSync = ZipOpenFS.prototype.fchownSync;
        this.closePromise = ZipOpenFS.prototype.closePromise;
        this.closeSync = ZipOpenFS.prototype.closeSync;
        this.createReadStream = ZipOpenFS.prototype.createReadStream;
        this.createWriteStream = ZipOpenFS.prototype.createWriteStream;
        this.existsPromise = ZipOpenFS.prototype.existsPromise;
        this.existsSync = ZipOpenFS.prototype.existsSync;
        this.fstatPromise = ZipOpenFS.prototype.fstatPromise;
        this.fstatSync = ZipOpenFS.prototype.fstatSync;
        this.getExtractHint = ZipOpenFS.prototype.getExtractHint;
        this.getRealPath = ZipOpenFS.prototype.getRealPath;
        this.linkPromise = ZipOpenFS.prototype.linkPromise;
        this.linkSync = ZipOpenFS.prototype.linkSync;
        this.lstatPromise = ZipOpenFS.prototype.lstatPromise;
        this.lstatSync = ZipOpenFS.prototype.lstatSync;
        this.openPromise = ZipOpenFS.prototype.openPromise;
        this.openSync = ZipOpenFS.prototype.openSync;
        this.readFilePromise = ZipOpenFS.prototype.readFilePromise;
        this.readFileSync = ZipOpenFS.prototype.readFileSync;
        this.readlinkPromise = ZipOpenFS.prototype.readlinkPromise;
        this.readlinkSync = ZipOpenFS.prototype.readlinkSync;
        this.readPromise = ZipOpenFS.prototype.readPromise;
        this.readSync = ZipOpenFS.prototype.readSync;
        this.renamePromise = ZipOpenFS.prototype.renamePromise;
        this.renameSync = ZipOpenFS.prototype.renameSync;
        this.resolve = ZipOpenFS.prototype.resolve;
        this.statPromise = ZipOpenFS.prototype.statPromise;
        this.statSync = ZipOpenFS.prototype.statSync;
        this.symlinkPromise = ZipOpenFS.prototype.symlinkPromise;
        this.symlinkSync = ZipOpenFS.prototype.symlinkSync;
        this.truncatePromise = ZipOpenFS.prototype.truncatePromise;
        this.truncateSync = ZipOpenFS.prototype.truncateSync;
        this.ftruncatePromise = ZipOpenFS.prototype.ftruncatePromise;
        this.ftruncateSync = ZipOpenFS.prototype.ftruncateSync;
        this.unlinkPromise = ZipOpenFS.prototype.unlinkPromise;
        this.unlinkSync = ZipOpenFS.prototype.unlinkSync;
        this.unwatchFile = ZipOpenFS.prototype.unwatchFile;
        this.utimesPromise = ZipOpenFS.prototype.utimesPromise;
        this.utimesSync = ZipOpenFS.prototype.utimesSync;
        this.lutimesPromise = ZipOpenFS.prototype.utimesPromise;
        this.lutimesSync = ZipOpenFS.prototype.utimesSync;
        this.watch = ZipOpenFS.prototype.watch;
        this.watchFile = ZipOpenFS.prototype.watchFile;
        this.writeFilePromise = ZipOpenFS.prototype.writeFilePromise;
        this.writeFileSync = ZipOpenFS.prototype.writeFileSync;
        this.writePromise = ZipOpenFS.prototype.writePromise;
        this.writeSync = ZipOpenFS.prototype.writeSync;
        // @ts-ignore
        this.remapFd = ZipOpenFS.prototype.remapFd;
        this.zipFs = opts.zipFs;
        this.baseFs = opts.baseFs;
        this.root = opts.root;
        this.magic = 0x2a << 24;
    }
    makeCallPromise(p, discard, accept, { requireSubpath = true } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof p !== 'string')
                return yield discard();
            const normalizedP = this.resolve(p);
            const zipInfo = this.findZip(normalizedP);
            if (!zipInfo)
                return yield discard();
            if (requireSubpath && zipInfo.subPath === '/')
                return yield discard();
            return yield accept(this.zipFs, zipInfo);
        });
    }
    makeCallSync(p, discard, accept, { requireSubpath = true } = {}) {
        if (typeof p !== 'string')
            return discard();
        const normalizedP = this.resolve(p);
        const zipInfo = this.findZip(normalizedP);
        if (!zipInfo)
            return discard();
        if (requireSubpath && zipInfo.subPath === '/')
            return discard();
        return accept(this.zipFs, Object.assign({ archivePath: '' }, zipInfo));
    }
    realpathPromise(p) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.realpathSync(p);
        });
    }
    realpathSync(p) {
        return this.makeCallSync(p, () => this.baseFs.realpathSync(p), (zipFs, { subPath }) => {
            if (zipFs.lstatSync(subPath).isSymbolicLink()) {
                return zipFs.realpathSync(subPath);
            }
            else {
                // return the original path in case it wasn't under /snapshot, e.g. if it was for a node module - otherwise the node module parent path is the wrong one (and other things resolve relative to that)
                return p;
            }
        });
    }
    findZip(p) {
        p = this.resolve(p);
        const snapshotPP = npath.toPortablePath('/snapshot');
        const pathsToTry = Array.from(new Set([
            p,
            resolve('/snapshot', p),
            ppath.resolve(snapshotPP, npath.toPortablePath(npath.relative(npath.fromPortablePath(this.root), npath.fromPortablePath(p)))),
            ppath.resolve(snapshotPP, npath.toPortablePath(npath.relative(toNamespacedPath(npath.fromPortablePath(this.root)), toNamespacedPath(npath.fromPortablePath(p))))),
            ppath.resolve(snapshotPP, npath.toPortablePath(npath.relative(npath.fromPortablePath(process.cwd()), npath.fromPortablePath(p)))),
            ppath.resolve(snapshotPP, npath.toPortablePath(npath.relative(toNamespacedPath(npath.fromPortablePath(process.cwd())), toNamespacedPath(npath.fromPortablePath(p))))),
        ]));
        for (const path of pathsToTry) {
            const portablePath = npath.toPortablePath(path);
            if (this.zipFs.existsSync(portablePath)) {
                return {
                    subPath: portablePath,
                };
            }
        }
    }
    copyFilePromise(sourceP, destP, flags = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            const fallback = (sourceFs, sourceP, destFs, destP) => __awaiter(this, void 0, void 0, function* () {
                if ((flags & constants.COPYFILE_FICLONE_FORCE) !== 0)
                    throw Object.assign(new Error(`EXDEV: cross-device clone not permitted, copyfile '${sourceP}' -> ${destP}'`), { code: `EXDEV` });
                if (flags & constants.COPYFILE_EXCL && (yield this.existsPromise(sourceP)))
                    throw Object.assign(new Error(`EEXIST: file already exists, copyfile '${sourceP}' -> '${destP}'`), { code: `EEXIST` });
                let content;
                try {
                    content = yield sourceFs.readFilePromise(sourceP);
                }
                catch (error) {
                    throw Object.assign(new Error(`EINVAL: invalid argument, copyfile '${sourceP}' -> '${destP}'`), { code: `EINVAL` });
                }
                yield destFs.writeFilePromise(destP, content);
            });
            return yield this.makeCallPromise(sourceP, () => __awaiter(this, void 0, void 0, function* () {
                return yield this.baseFs.copyFilePromise(sourceP, destP, flags);
            }), (zipFsS, { subPath: subPathS }) => __awaiter(this, void 0, void 0, function* () {
                return yield fallback(zipFsS, subPathS, this.baseFs, destP);
            }));
        });
    }
    copyFileSync(sourceP, destP, flags = 0) {
        const fallback = (sourceFs, sourceP, destFs, destP) => {
            if ((flags & constants.COPYFILE_FICLONE_FORCE) !== 0)
                throw Object.assign(new Error(`EXDEV: cross-device clone not permitted, copyfile '${sourceP}' -> ${destP}'`), { code: `EXDEV` });
            if (flags & constants.COPYFILE_EXCL && this.existsSync(sourceP))
                throw Object.assign(new Error(`EEXIST: file already exists, copyfile '${sourceP}' -> '${destP}'`), { code: `EEXIST` });
            let content;
            try {
                content = sourceFs.readFileSync(sourceP);
            }
            catch (error) {
                throw Object.assign(new Error(`EINVAL: invalid argument, copyfile '${sourceP}' -> '${destP}'`), { code: `EINVAL` });
            }
            destFs.writeFileSync(destP, content);
        };
        return this.makeCallSync(sourceP, () => {
            return this.baseFs.copyFileSync(sourceP, destP, flags);
        }, (zipFsS, { subPath: subPathS }) => {
            return fallback(zipFsS, subPathS, this.baseFs, destP);
        });
    }
    readdirPromise(p, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const fallback = () => __awaiter(this, void 0, void 0, function* () {
                return yield this.baseFs.readdirPromise(p, opts);
            });
            return yield this.makeCallPromise(p, fallback, (zipFs, { subPath }) => __awaiter(this, void 0, void 0, function* () {
                const fallbackPaths = yield fallback().catch(() => []);
                return Promise.resolve(uniqReaddir(fallbackPaths.concat(yield zipFs.readdirPromise(subPath, opts))));
            }), {
                requireSubpath: false,
            });
        });
    }
    readdirSync(p, opts) {
        const fallback = () => {
            return this.baseFs.readdirSync(p, opts);
        };
        return this.makeCallSync(p, fallback, (zipFs, { subPath }) => {
            let fallbackPaths = [];
            try {
                fallbackPaths = fallback();
            }
            catch (e) { }
            return fallbackPaths.concat(uniqReaddir(zipFs.readdirSync(subPath, opts)));
        }, {
            requireSubpath: false,
        });
    }
    mkdirPromise(p, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.baseFs.mkdirPromise(p, opts);
        });
    }
    mkdirSync(p, opts) {
        return this.baseFs.mkdirSync(p, opts);
    }
    rmdirPromise(p, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.baseFs.rmdirPromise(p, opts);
        });
    }
    rmdirSync(p, opts) {
        return this.baseFs.rmdirSync(p, opts);
    }
    opendirPromise(p, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.opendirSync(p, opts);
        });
    }
    opendirSync(p, opts) {
        const zipInfo = this.findZip(p);
        let zipFsDir = null;
        if (zipInfo) {
            zipFsDir = this.zipFs.opendirSync(zipInfo.subPath);
        }
        let realFsDir = null;
        try {
            realFsDir = this.baseFs.opendirSync(p);
        }
        catch (e) {
            if (!zipFsDir)
                throw e;
        }
        const seen = new Set();
        const nextDirent = () => {
            const entry = (realFsDir === null || realFsDir === void 0 ? void 0 : realFsDir.readSync()) || (zipFsDir === null || zipFsDir === void 0 ? void 0 : zipFsDir.readSync());
            if (entry && !seen.has(entry.name)) {
                seen.add(entry.name);
                return entry;
            }
            return null;
        };
        const onClose = () => {
            zipFsDir === null || zipFsDir === void 0 ? void 0 : zipFsDir.closeSync();
            realFsDir === null || realFsDir === void 0 ? void 0 : realFsDir.closeSync();
        };
        return new CustomDir(p, nextDirent, { onClose });
    }
}
